import { CookieIcon, InfoIcon, LockIcon, ScaleIcon, SettingsIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useLang } from '../hooks/useLang';
import { NAV } from '../navConfig';
import { t } from '../../i18n';
import type { ViewId } from '../../types';
import type { LegalKind } from '../views/LegalSheet';

/** Desktop only — the persistent sidebar. Mobile navigation is the hamburger
 *  drawer (`NavDrawer.tsx`) alone; this used to also render a bottom tab bar
 *  on mobile, removed after tester feedback that tab → contextual-submenu
 *  was an awkward extra layer of indirection. */
export function TabBar({
  current,
  onNav,
  onSettings,
  onAbout,
  onLegal,
  onCookieSettings,
}: {
  current: ViewId;
  onNav: (id: ViewId) => void;
  onSettings: () => void;
  onAbout: () => void;
  onLegal: (kind: LegalKind) => void;
  onCookieSettings: () => void;
}) {
  useLang();
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <nav aria-label={t('app.nav')} className="fixed inset-y-0 left-0 z-50 flex w-[var(--nav-w)] flex-col gap-1 border-r bg-card">
        <button
          type="button"
          onClick={() => onNav('home')}
          title={t('nav.home')}
          className="border-b px-4.5 py-5 text-left transition-colors hover:bg-secondary"
        >
          <p className="text-[17px] font-bold tracking-tight text-foreground">{t('app.title')}</p>
          <p className="mt-0.5 font-mono text-[9.5px] font-medium tracking-widest text-muted-foreground uppercase">{t('app.subtitle')}</p>
        </button>
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = item.id === current;
          return (
            <button
              key={item.id}
              type="button"
              aria-current={active ? 'page' : 'false'}
              onClick={() => onNav(item.id)}
              className={cn(
                'flex items-center gap-3 border-l-2 border-transparent px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary',
                active && 'border-brand bg-secondary font-medium text-foreground',
              )}
            >
              <Icon className="size-5 shrink-0" />
              <span>{t(item.labelKey)}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={onSettings}
          className="mt-auto flex items-center gap-3 border-t px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
        >
          <SettingsIcon className="size-5 shrink-0" />
          <span>{t('app.settings')}</span>
        </button>
        <button
          type="button"
          onClick={onAbout}
          className="flex items-center gap-3 px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
        >
          <InfoIcon className="size-5 shrink-0" />
          <span>{t('app.about')}</span>
        </button>
        <button
          type="button"
          onClick={() => onLegal('impressum')}
          className="flex items-center gap-3 px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
        >
          <ScaleIcon className="size-5 shrink-0" />
          <span>{t('nav.impressum')}</span>
        </button>
        <button
          type="button"
          onClick={() => onLegal('datenschutz')}
          className="flex items-center gap-3 px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
        >
          <LockIcon className="size-5 shrink-0" />
          <span>{t('nav.datenschutz')}</span>
        </button>
        <button
          type="button"
          onClick={onCookieSettings}
          className="flex items-center gap-3 px-4.5 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
        >
          <CookieIcon className="size-5 shrink-0" />
          <span>{t('consent.title')}</span>
        </button>
      </nav>
    );
  }

  return null;
}
